/**
 * Rendering a document number as a string, and nothing else.
 *
 * Specification: ../../test/numbering.spec.ts. Master plan §2 D7, §4 M7.
 *
 * SPLIT OUT OF `./index.ts` SO IT CAN BE IMPORTED BY THE TILL AND THE EVENT
 * BOUNDARY, which is the whole reason this file exists rather than one more
 * export next door.
 *
 * `./index.ts` imports `createTenantTable`, a VALUE, which reaches the tenancy
 * module and therefore the postgres driver. Anything importing it inherits
 * that: the orders event definitions would have dragged the driver into
 * `apps/pos`, and so would the till's own receipt numbering. Tree-shaking does
 * not save you here — a `CREATE TABLE` template literal is not provably pure —
 * and this is the third time server-only code has leaked toward the till this
 * way (module descriptors, then `kernel/config`'s `ConfigCache`). The answer
 * each time was to split the module, so it is split before the leak rather
 * than after.
 *
 * THIS FILE IMPORTS NOTHING. That is a property worth keeping.
 */

/** Its own error, so the split does not require importing `NumberingError`. */
export class DocumentNumberError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DocumentNumberError";
  }
}

/**
 * `porad_cis`, EET 2.0 interface description §3.3.3.10.
 *
 * The XSD says `{1,25}`; the prose three lines below the mask says 1 to 20. THE
 * DOCUMENT CONTRADICTS ITSELF and the tighter bound wins, for the reason the
 * adapter gives: a number the schema accepts and a downstream check rejects is
 * a sale reported and then queried, while one refused here is a configuration
 * problem found before anything is filed.
 *
 * RESTATED rather than imported, because the kernel must not depend on a
 * jurisdiction adapter — CI forbids it, and rightly: the kernel does not know
 * what Czech is. A restated constant drifts, so `@gf/adapter-cz-eet`'s own spec
 * feeds this function's output through `buildTrzba` across a sweep of widths.
 * That test is the only place the two copies can be caught disagreeing.
 */
export const DOCUMENT_NUMBER_MAX = 20;

/** Zero-padded digits, so the string sorts. Six is the platform default. */
export const DOCUMENT_NUMBER_WIDTH = 6;

export interface DocumentNumberParts {
  readonly period: string;
  readonly number: number;
  readonly width?: number;
}

/**
 * A document number as it is printed, filed and sorted — `"2027-000417"`.
 *
 * Nothing formatted one before this: the fiscal projection asked for a string
 * and every caller improvised, which is how a series ends up half-padded.
 *
 * THE WIDTH IS FIXED BECAUSE THE STRING HAS TO SORT. Pad "as needed" and
 * `"2027-100"` orders before `"2027-99"` in SQL, in a spreadsheet and in a
 * directory of exported documents — and an auditor reading a mis-sorted series
 * sees gaps that are not there.
 *
 * It is also the function the EVENT BOUNDARY re-derives with. A till sends both
 * the parts and the rendered string; the boundary renders the parts again and
 * refuses a mismatch, the same move as the `priced` balance check. That only
 * works because there is exactly one implementation and one default width.
 */
export function formatDocumentNumber(args: DocumentNumberParts): string {
  const width = args.width ?? DOCUMENT_NUMBER_WIDTH;

  if (!Number.isInteger(args.number) || args.number < 1) {
    throw new DocumentNumberError(
      `a document number must be a positive integer, got ${args.number}`,
    );
  }

  const digits = String(args.number);

  if (digits.length > width) {
    /**
     * REFUSED, NOT WIDENED. Widening is the tempting default and it destroys
     * the one property the padding exists for: `"2027-1000000"` sorts before
     * `"2027-0999999"`. A venue that has issued a million receipts in a single
     * year has outgrown the width, and changing the format halfway through a
     * numbered series is worse than refusing to — the series is the thing an
     * auditor reads end to end.
     */
    throw new DocumentNumberError(
      `number ${args.number} does not fit ${width} digits. Widening it would break the ` +
        `lexical ordering the fixed width exists for, so the format is refused rather than ` +
        `changed halfway through a series.`,
    );
  }

  const formatted = `${args.period}-${digits.padStart(width, "0")}`;

  if (formatted.length > DOCUMENT_NUMBER_MAX) {
    throw new DocumentNumberError(
      `document number ${JSON.stringify(formatted)} is ${formatted.length} characters; the ` +
        `tightest fiscal adapter we ship accepts ${DOCUMENT_NUMBER_MAX} (EET 2.0 porad_cis, ` +
        `interface description §3.3.3.10). A number the schema rejects is a sale that cannot ` +
        `be reported at all.`,
    );
  }

  return formatted;
}
